import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const Backdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 40px;
  
  @media (max-width: 768px) {
    padding: 30px;
  }
  
  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const Modal = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  color: #333;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 30px;
  text-align: center;
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SettingsForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SettingsGroup = styled.div`
  background: #f5f5f5;
  border-radius: 15px;
  padding: 20px;
`;

const SettingItem = styled.div`
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: bold;
  margin-bottom: 8px;
  color: #444;
`;

const Select = styled.select`
  padding: 10px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  font-size: 16px;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: #4ecdc4;
  }
`;

const Toggle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 34px;
    
    &:before {
      position: absolute;
      content: "";
      height: 26px;
      width: 26px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }
  }
  
  input:checked + span {
    background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  }
  
  input:checked + span:before {
    transform: translateX(26px);
  }
`;

const ToggleLabel = styled.div`
  font-size: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 12px 30px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &.primary {
    background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
    color: white;
  }
  
  &.secondary {
    background: #e0e0e0;
    color: #333;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const RadioButton = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  background: ${props => props.checked ? 'rgba(78, 205, 196, 0.3)' : 'rgba(255, 255, 255, 0.7)'};
  border: 2px solid ${props => props.checked ? '#4ecdc4' : '#e0e0e0'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(78, 205, 196, 0.2);
  }
  
  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const GameSettings = ({ settings, onSave, onClose }) => {
  const { isPremium } = useAuth();
  const [gameSettings, setGameSettings] = useState(settings || {
    wheelSize: 'medium',
    allowPlayersToSpin: true,
    wheelTheme: 'classic',
    turnMode: 'sequential' // Default: 'sequential' (next player) or 'random'
  });

  const handleChange = (field, value) => {
    setGameSettings({
      ...gameSettings,
      [field]: value
    });
  };

  const handleSave = (e) => {
    e.preventDefault();
    onSave(gameSettings);
  };

  return (
    <AnimatePresence>
      <Backdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Modal
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Title>Game Settings ⚙️</Title>
          
          <SettingsForm onSubmit={handleSave}>
            <SettingsGroup>
              <SettingItem>
                <Label>Wheel Size</Label>
                <Select 
                  value={gameSettings.wheelSize}
                  onChange={(e) => handleChange('wheelSize', e.target.value)}
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </Select>
              </SettingItem>
              
              <SettingItem>
                <Label>Who Can Spin The Wheel</Label>
                <Toggle>
                  <ToggleSwitch>
                    <input 
                      type="checkbox" 
                      checked={gameSettings.allowPlayersToSpin}
                      onChange={(e) => handleChange('allowPlayersToSpin', e.target.checked)}
                    />
                    <span></span>
                  </ToggleSwitch>
                  <ToggleLabel>
                    {gameSettings.allowPlayersToSpin ? 'All Players (Turn Based)' : 'Host Only'}
                  </ToggleLabel>
                </Toggle>
              </SettingItem>
              
              <SettingItem>
                <Label>Turn Order</Label>
                <RadioGroup>
                  <RadioButton checked={gameSettings.turnMode === 'sequential'}>
                    <input
                      type="radio"
                      name="turnMode"
                      value="sequential"
                      checked={gameSettings.turnMode === 'sequential'}
                      onChange={() => handleChange('turnMode', 'sequential')}
                    />
                    Sequential (Next Player in List) - Default
                  </RadioButton>
                  <RadioButton checked={gameSettings.turnMode === 'random'}>
                    <input
                      type="radio"
                      name="turnMode"
                      value="random"
                      checked={gameSettings.turnMode === 'random'}
                      onChange={() => handleChange('turnMode', 'random')}
                    />
                    Random Player
                  </RadioButton>
                </RadioGroup>
              </SettingItem>
            </SettingsGroup>
            
            <SettingsGroup>
              <SettingItem>
                <Label>Wheel Theme</Label>
                <RadioGroup>
                  <RadioButton checked={gameSettings.wheelTheme === 'classic'}>
                    <input
                      type="radio"
                      name="wheelTheme"
                      value="classic"
                      checked={gameSettings.wheelTheme === 'classic'}
                      onChange={() => handleChange('wheelTheme', 'classic')}
                    />
                    🎯 Classic (Free)
                  </RadioButton>
                  <RadioButton 
                    checked={gameSettings.wheelTheme === 'neon'}
                    style={{ 
                      opacity: !isPremium ? 0.6 : 1,
                      cursor: !isPremium ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <input
                      type="radio"
                      name="wheelTheme"
                      value="neon"
                      checked={gameSettings.wheelTheme === 'neon'}
                      onChange={() => {
                        if (!isPremium) {
                          alert('Premium themes are only available with Premium subscription!');
                          return;
                        }
                        handleChange('wheelTheme', 'neon');
                      }}
                      disabled={!isPremium}
                    />
                    ⚡ Neon {!isPremium && '(Premium)'}
                  </RadioButton>
                  <RadioButton 
                    checked={gameSettings.wheelTheme === 'pastel'}
                    style={{ 
                      opacity: !isPremium ? 0.6 : 1,
                      cursor: !isPremium ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <input
                      type="radio"
                      name="wheelTheme"
                      value="pastel"
                      checked={gameSettings.wheelTheme === 'pastel'}
                      onChange={() => {
                        if (!isPremium) {
                          alert('Premium themes are only available with Premium subscription!');
                          return;
                        }
                        handleChange('wheelTheme', 'pastel');
                      }}
                      disabled={!isPremium}
                    />
                    🌸 Pastel {!isPremium && '(Premium)'}
                  </RadioButton>
                  <RadioButton 
                    checked={gameSettings.wheelTheme === 'monochrome'}
                    style={{ 
                      opacity: !isPremium ? 0.6 : 1,
                      cursor: !isPremium ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <input
                      type="radio"
                      name="wheelTheme"
                      value="monochrome"
                      checked={gameSettings.wheelTheme === 'monochrome'}
                      onChange={() => {
                        if (!isPremium) {
                          alert('Premium themes are only available with Premium subscription!');
                          return;
                        }
                        handleChange('wheelTheme', 'monochrome');
                      }}
                      disabled={!isPremium}
                    />
                    ⚫ Monochrome {!isPremium && '(Premium)'}
                  </RadioButton>
                  <RadioButton 
                    checked={gameSettings.wheelTheme === 'galaxy'}
                    style={{ 
                      opacity: !isPremium ? 0.6 : 1,
                      cursor: !isPremium ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <input
                      type="radio"
                      name="wheelTheme"
                      value="galaxy"
                      checked={gameSettings.wheelTheme === 'galaxy'}
                      onChange={() => {
                        if (!isPremium) {
                          alert('Premium themes are only available with Premium subscription!');
                          return;
                        }
                        handleChange('wheelTheme', 'galaxy');
                      }}
                      disabled={!isPremium}
                    />
                    🌌 Galaxy {!isPremium && '(Premium)'}
                  </RadioButton>
                  <RadioButton 
                    checked={gameSettings.wheelTheme === 'ocean'}
                    style={{ 
                      opacity: !isPremium ? 0.6 : 1,
                      cursor: !isPremium ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <input
                      type="radio"
                      name="wheelTheme"
                      value="ocean"
                      checked={gameSettings.wheelTheme === 'ocean'}
                      onChange={() => {
                        if (!isPremium) {
                          alert('Premium themes are only available with Premium subscription!');
                          return;
                        }
                        handleChange('wheelTheme', 'ocean');
                      }}
                      disabled={!isPremium}
                    />
                    🌊 Ocean {!isPremium && '(Premium)'}
                  </RadioButton>
                  <RadioButton 
                    checked={gameSettings.wheelTheme === 'fire'}
                    style={{ 
                      opacity: !isPremium ? 0.6 : 1,
                      cursor: !isPremium ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <input
                      type="radio"
                      name="wheelTheme"
                      value="fire"
                      checked={gameSettings.wheelTheme === 'fire'}
                      onChange={() => {
                        if (!isPremium) {
                          alert('Premium themes are only available with Premium subscription!');
                          return;
                        }
                        handleChange('wheelTheme', 'fire');
                      }}
                      disabled={!isPremium}
                    />
                    🔥 Fire {!isPremium && '(Premium)'}
                  </RadioButton>
                  <RadioButton 
                    checked={gameSettings.wheelTheme === 'forest'}
                    style={{ 
                      opacity: !isPremium ? 0.6 : 1,
                      cursor: !isPremium ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <input
                      type="radio"
                      name="wheelTheme"
                      value="forest"
                      checked={gameSettings.wheelTheme === 'forest'}
                      onChange={() => {
                        if (!isPremium) {
                          alert('Premium themes are only available with Premium subscription!');
                          return;
                        }
                        handleChange('wheelTheme', 'forest');
                      }}
                      disabled={!isPremium}
                    />
                    🌲 Forest {!isPremium && '(Premium)'}
                  </RadioButton>
                  <RadioButton 
                    checked={gameSettings.wheelTheme === 'retro'}
                    style={{ 
                      opacity: !isPremium ? 0.6 : 1,
                      cursor: !isPremium ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <input
                      type="radio"
                      name="wheelTheme"
                      value="retro"
                      checked={gameSettings.wheelTheme === 'retro'}
                      onChange={() => {
                        if (!isPremium) {
                          alert('Premium themes are only available with Premium subscription!');
                          return;
                        }
                        handleChange('wheelTheme', 'retro');
                      }}
                      disabled={!isPremium}
                    />
                    🕹️ Retro {!isPremium && '(Premium)'}
                  </RadioButton>
                </RadioGroup>
                
                {!isPremium && (
                  <div style={{
                    background: 'linear-gradient(45deg, #4ecdc4, #45b7d1)',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    marginTop: '15px',
                    textAlign: 'center',
                    fontSize: '14px'
                  }}>
                    🎨 Unlock premium themes with Premium subscription!
                  </div>
                )}
              </SettingItem>
            </SettingsGroup>
            
            <ButtonGroup>
              <Button 
                type="button" 
                className="secondary" 
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="primary"
              >
                Save Settings
              </Button>
            </ButtonGroup>
          </SettingsForm>
        </Modal>
      </Backdrop>
    </AnimatePresence>
  );
};

export default GameSettings; 